import { DateTime } from "luxon"
import { eventQrStatusStyles } from "../../utils/eventQr"
import { useAuthUser } from "../../hooks/useAuth"
import { userRoles } from "../../constants"

// The API lets DEBUG read these pages but only ADMIN change them.
export const useCanEditEventQr = () => {
  const { data } = useAuthUser()
  return (data?.data?.role || []).includes(userRoles.ADMIN)
}

export const ToggleSwitch = ({ checked, disabled, onChange, label }) => (
  <button
    aria-checked={checked}
    aria-label={label}
    className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${
      checked ? "bg-green-600" : "bg-gray-300"
    }`}
    disabled={disabled}
    role="switch"
    type="button"
    onClick={onChange}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
)

export const EventStatusPill = ({ status }) => {
  const style = eventQrStatusStyles[status]
  if (!style) return null

  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  )
}

export const ActivePill = ({ isActive }) => (
  <span
    className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
      isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
    }`}
  >
    {isActive ? "Active" : "Off"}
  </span>
)

// API dates are UTC ISO strings; admins see them in their own time zone.
export const formatEventDate = (iso, fallback = "—") =>
  iso ? DateTime.fromISO(iso).toFormat("MMM d, yyyy h:mm a") : fallback

export const TableHeaderCell = ({ children, className = "" }) => (
  <th
    className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${className}`}
  >
    {children}
  </th>
)

export const TableCell = ({ children, className = "", ...props }) => (
  <td className={`px-4 py-3 text-sm text-gray-700 ${className}`} {...props}>
    {children}
  </td>
)

const ColGroup = ({ columns }) => (
  <colgroup>
    {columns.map((column) => (
      <col key={column.key} className={column.width} />
    ))}
  </colgroup>
)

/*
  A table whose body scrolls under a fixed header, sized by its flex parent.

  The header and body are two tables sharing one <colgroup>, so only the body
  scrolls and its scrollbar never covers the header. Both reserve the same
  scrollbar gutter, which keeps their columns lined up whether or not the body
  overflows. min-h-0 lets the card shrink to the space its parent gives it.

  `columns`: [{ key, label, width: "w-[30%]", className }] — className aligns
  the header cell (e.g. "text-right").
*/
export const ScrollTable = ({ columns, children }) => (
  <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="shrink-0 overflow-hidden border-b border-gray-200 bg-gray-50 [scrollbar-gutter:stable]">
      <table className="w-full table-fixed">
        <ColGroup columns={columns} />
        <thead>
          <tr>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={column.className}>
                {column.label}
              </TableHeaderCell>
            ))}
          </tr>
        </thead>
      </table>
    </div>
    <div className="min-h-0 overflow-y-auto [scrollbar-gutter:stable]">
      <table className="w-full table-fixed">
        <ColGroup columns={columns} />
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
    </div>
  </div>
)

const PAGE_SIZES = [10, 20, 50, 100]

const pageButtonClass =
  "rounded-md bg-secondary px-2.5 py-1 text-xs text-white shadow hover:bg-secondary_dark disabled:cursor-not-allowed disabled:bg-gray-300"

// Footer for a server-paginated table. `page` is 1-based.
export const TablePagination = ({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / perPage))
  const from = total ? (page - 1) * perPage + 1 : 0
  const to = Math.min(page * perPage, total)

  return (
    <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 pt-3 text-xs text-gray-600">
      <span className="tabular-nums">
        {total ? `${from}–${to} of ${total}` : "No results"}
      </span>
      <div className="flex items-center gap-2">
        <button
          className={pageButtonClass}
          disabled={page <= 1}
          type="button"
          onClick={() => onPageChange(1)}
        >
          First
        </button>
        <button
          className={pageButtonClass}
          disabled={page <= 1}
          type="button"
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span className="px-1 tabular-nums">
          Page {page} of {pageCount}
        </span>
        <button
          className={pageButtonClass}
          disabled={page >= pageCount}
          type="button"
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
        <button
          className={pageButtonClass}
          disabled={page >= pageCount}
          type="button"
          onClick={() => onPageChange(pageCount)}
        >
          Last
        </button>
        <select
          aria-label="Rows per page"
          className="ml-2 rounded-md border border-gray-300 bg-white px-2 py-1"
          value={perPage}
          onChange={(event) => onPerPageChange(Number(event.target.value))}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      </div>
    </footer>
  )
}

export const TableMessageRow = ({ colSpan, children }) => (
  <tr>
    <td
      className="px-4 py-10 text-center text-sm text-gray-400"
      colSpan={colSpan}
    >
      {children}
    </td>
  </tr>
)
